import { formatEuro } from './format';

export type LeaderboardSource = 'firebase' | 'local' | 'archive';
export type LeaderboardStatusState = 'idle' | 'loading' | 'ready' | 'fallback' | 'error';

export interface LeaderboardRowView {
  readonly id: string;
  readonly rank: number;
  readonly name: string;
  readonly xValue: number;
  readonly profitCents: number;
  readonly betCents: number;
  readonly replayAvailable: boolean;
  readonly featureCount?: number;
  readonly totalSpins?: number;
}

export type LeaderboardDisplayMode = 'ranked' | 'extreme';
export type LeaderboardDisplayBoard = 'runs' | 'freeSpins';

export interface LeaderboardStatusView {
  readonly state: LeaderboardStatusState;
  readonly message: string;
  readonly source: LeaderboardSource;
}

export interface LeaderboardViewPort {
  configure(mode: LeaderboardDisplayMode, board: LeaderboardDisplayBoard): void;
  renderRows(rows: readonly LeaderboardRowView[], options?: { readonly archived?: boolean }): void;
  renderStatus(status: LeaderboardStatusView): void;
  setReplayHandler(handler: (entryId: string) => void): void;
}

export function formatLeaderboardRow(row: LeaderboardRowView): readonly string[] {
  return [
    String(row.rank),
    row.name,
    `${row.xValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`,
    formatEuro(row.profitCents),
    formatEuro(row.betCents),
  ];
}

export class LeaderboardView implements LeaderboardViewPort {
  private readonly body: HTMLTableSectionElement;
  private readonly status: HTMLElement;
  private readonly head: HTMLTableRowElement;
  private readonly context: HTMLElement;
  private mode: LeaderboardDisplayMode = 'ranked';
  private board: LeaderboardDisplayBoard = 'runs';
  private headings: readonly string[] = ['Rang', 'Duellant', 'X-Wert', 'Gewinn', 'Einsatz', 'Replay'];
  private replayHandler: (entryId: string) => void = () => undefined;

  constructor(root: Document) {
    const body = root.querySelector<HTMLTableSectionElement>('[data-leaderboard-body]');
    const status = root.querySelector<HTMLElement>('[data-leaderboard-status]');
    const head = root.querySelector<HTMLTableRowElement>('[data-leaderboard-head]');
    const context = root.querySelector<HTMLElement>('[data-leaderboard-context]');
    if (body === null || status === null || head === null || context === null) throw new Error('Ranglistenansicht ist unvollständig.');
    this.body = body;
    this.status = status;
    this.head = head;
    this.context = context;
  }

  configure(mode: LeaderboardDisplayMode, board: LeaderboardDisplayBoard): void {
    this.mode = mode;
    this.board = board;
    this.context.hidden = mode !== 'extreme';
    const headings = mode === 'ranked'
      ? ['Rang', 'Duellant', 'X-Wert', 'Gewinn', 'Einsatz', 'Replay']
      : board === 'runs'
        ? ['Rang', 'Duellant', 'Score-X', 'Endguthaben', 'Features']
        : ['Rang', 'Duellant', 'X-Wert', 'Gewinn', 'Freispiele', 'Replay'];
    this.headings = headings;
    this.head.replaceChildren(...headings.map((label) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = label;
      return cell;
    }));
  }

  setReplayHandler(handler: (entryId: string) => void): void {
    this.replayHandler = handler;
  }

  renderStatus(status: LeaderboardStatusView): void {
    this.status.dataset.state = status.state;
    this.status.dataset.source = status.source;
    this.status.textContent = status.message;
  }

  renderRows(rows: readonly LeaderboardRowView[], options: { readonly archived?: boolean } = {}): void {
    this.body.replaceChildren();
    if (rows.length === 0) {
      const row = document.createElement('tr');
      const cell = document.createElement('td');
      cell.colSpan = this.headings.length;
      cell.textContent = options.archived === true
        ? 'Im Archiv sind keine Einträge vorhanden.'
        : 'Noch keine Ranglisteneinträge vorhanden.';
      row.append(cell);
      this.body.append(row);
      return;
    }
    rows.slice(0, 10).forEach((entry) => {
      const row = document.createElement('tr');
      const cells = this.mode === 'ranked'
        ? formatLeaderboardRow(entry)
        : [
          String(entry.rank),
          entry.name,
          `${entry.xValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`,
          formatEuro(entry.profitCents),
          String(this.board === 'runs' ? entry.featureCount ?? 0 : entry.totalSpins ?? 0),
        ];
      cells.forEach((copy, index) => {
        const cell = document.createElement('td');
        cell.textContent = copy;
        cell.dataset.label = this.headings[index] ?? '';
        row.append(cell);
      });
      if (this.headings.includes('Replay')) {
        const replayCell = document.createElement('td');
        replayCell.dataset.label = 'Replay';
        if (entry.replayAvailable) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'secondary-button';
          button.textContent = 'Ansehen';
          button.setAttribute('aria-label', `Replay von ${entry.name} ansehen`);
          button.addEventListener('click', () => this.replayHandler(entry.id));
          replayCell.append(button);
        } else {
          replayCell.textContent = '—';
        }
        row.append(replayCell);
      }
      this.body.append(row);
    });
  }
}
